// PerspectiveCropCommand — implements Photoshop's Perspective Crop.
//
// Given 4 user-placed corners (TL, TR, BL, BR — in any order the user placed
// them) the command:
//   1. Sorts the corners into canonical order (top-left, top-right,
//      bottom-left, bottom-right) by angle from centroid.
//   2. Computes output dimensions from the average of the two horizontal and
//      two vertical edge lengths.
//   3. Applies an inverse perspective (projective) warp to every raster layer:
//      for each output pixel, the inverse homography maps back to source space
//      and samples the layer via bilinear interpolation.
//   4. Sets the new document canvas size to (outW, outH).
//
// Layer: 3 (Document Model). MUST remain DOM-free / browser-free.

import type { Document } from "../document/Document";
import type { GroupLayer, Layer, RasterLayer } from "../document/Layer";
import { PixelBuffer } from "../pixel/PixelBuffer";
import { Selection } from "../selection/Selection";
import type { Command } from "./Command";

export interface Quad {
  x0: number; y0: number; // top-left
  x1: number; y1: number; // top-right
  x2: number; y2: number; // bottom-left
  x3: number; y3: number; // bottom-right
}

interface PreSnapshot {
  readonly layers: readonly Layer[];
  readonly width: number;
  readonly height: number;
}

// ---------------------------------------------------------------------------
// Public command class
// ---------------------------------------------------------------------------

export class PerspectiveCropCommand implements Command {
  readonly kind = "perspective-crop";
  private snapshot: PreSnapshot | null = null;

  /** outW / outH: intended output canvas dimensions. */
  constructor(
    private readonly quad: Quad,
    private readonly outW: number,
    private readonly outH: number,
  ) {}

  apply(doc: Document): Document {
    if (!this.snapshot) {
      // Snapshot the full layer tree for undo.
      this.snapshot = {
        layers: doc.layers,
        width: doc.width,
        height: doc.height,
      };
    }

    // Compute the 3×3 perspective matrix H that maps output → source.
    // (We use the inverse homography for the inverse-map sampling.)
    const H = computeHomography(this.quad, this.outW, this.outH);

    const newLayers = warpLayerTree(doc.layers, H, this.outW, this.outH);

    return {
      ...doc,
      width: this.outW,
      height: this.outH,
      layers: newLayers,
      selection: doc.selection ? Selection.empty(this.outW, this.outH) : null,
    };
  }

  invert(before: Document, _after: Document): Command {
    const snap = this.snapshot;
    if (!snap) return new NullCommand();
    return new RestorePerspectiveCropCommand(
      snap.layers,
      snap.width,
      snap.height,
      before.selection ?? null,
    );
  }
}

// ---------------------------------------------------------------------------
// Restore command for undo
// ---------------------------------------------------------------------------

class NullCommand implements Command {
  readonly kind = "null";
  apply(doc: Document): Document { return doc; }
  invert(_before: Document, _after: Document): Command { return this; }
}

class RestorePerspectiveCropCommand implements Command {
  readonly kind = "restore-perspective-crop";
  constructor(
    private readonly layers: readonly Layer[],
    private readonly width: number,
    private readonly height: number,
    private readonly selection: Selection | null,
  ) {}

  apply(doc: Document): Document {
    return {
      ...doc,
      width: this.width,
      height: this.height,
      layers: this.layers,
      selection: this.selection,
    };
  }

  invert(before: Document, _after: Document): Command {
    return new RestorePerspectiveCropCommand(
      before.layers,
      before.width,
      before.height,
      before.selection ?? null,
    );
  }
}

// ---------------------------------------------------------------------------
// Perspective warp helpers
// ---------------------------------------------------------------------------

/**
 * Compute the 3×3 homography matrix that maps (output) → (source) coords.
 *
 * The output rectangle has corners (0,0), (outW,0), (0,outH), (outW,outH).
 * The source corners are defined by `quad` (TL, TR, BL, BR in source space).
 *
 * We solve the Direct Linear Transform (DLT) system for the 8 dof of H
 * such that H * srcCorner = outCorner (up to scale), then invert the result
 * so we can use it for inverse-mapping.
 */
function computeHomography(q: Quad, outW: number, outH: number): HomographyMatrix {
  // Destination corners: output-space (columns → x, rows → y).
  // TL=(0,0) TR=(outW,0) BL=(0,outH) BR=(outW,outH).
  // Source corners from quad.
  //
  // We compute H such that:   dst_i = H * src_i   (homogeneous)
  // Then for the inverse-map: src = Hinv * dst
  //
  // The DLT requires 4 point correspondences.

  const srcPts: [number, number][] = [
    [q.x0, q.y0],
    [q.x1, q.y1],
    [q.x2, q.y2],
    [q.x3, q.y3],
  ];
  const dstPts: [number, number][] = [
    [0, 0],
    [outW, 0],
    [0, outH],
    [outW, outH],
  ];

  // Build the 8×8 DLT matrix (homogeneous, 4 correspondences × 2 equations each,
  // normalised to h33=1).
  // For each pair (xi, yi) → (xi', yi'):
  //   xi' = (h0*xi + h1*yi + h2) / (h6*xi + h7*yi + 1)
  //   yi' = (h3*xi + h4*yi + h5) / (h6*xi + h7*yi + 1)
  // Rearranged:
  //   [ xi  yi  1   0   0   0  -xi'*xi  -xi'*yi ] [h0..h7]T = xi'
  //   [  0   0   0  xi  yi  1  -yi'*xi  -yi'*yi ] [h0..h7]T = yi'
  const A: number[][] = [];
  const b: number[] = [];

  for (let i = 0; i < 4; i++) {
    const [sx, sy] = srcPts[i]!;
    const [dx, dy] = dstPts[i]!;
    A.push([sx, sy, 1, 0,  0,  0, -dx * sx, -dx * sy]);
    A.push([0,  0,  0, sx, sy, 1, -dy * sx, -dy * sy]);
    b.push(dx);
    b.push(dy);
  }

  // Solve 8×8 linear system Ah=b using Gaussian elimination.
  const h = gaussSolve(A, b);
  if (!h) {
    // Degenerate — return identity-like (pass-through).
    return {
      h00: 1, h01: 0, h02: 0,
      h10: 0, h11: 1, h12: 0,
      h20: 0, h21: 0,
    };
  }

  // This H maps src → dst. We need the inverse for sampling dst → src.
  const fwd: HomographyMatrix = {
    h00: h[0]!, h01: h[1]!, h02: h[2]!,
    h10: h[3]!, h11: h[4]!, h12: h[5]!,
    h20: h[6]!, h21: h[7]!,
  };
  return invertHomography(fwd);
}

interface HomographyMatrix {
  h00: number; h01: number; h02: number;
  h10: number; h11: number; h12: number;
  h20: number; h21: number;
  // h22 is implicitly 1 (normalised)
}

function invertHomography(H: HomographyMatrix): HomographyMatrix {
  // Build full 3×3 and invert.
  const { h00, h01, h02, h10, h11, h12, h20, h21 } = H;
  const h22 = 1;

  // Cofactor expansion.
  const A00 = h11 * h22 - h12 * h21;
  const A01 = -(h10 * h22 - h12 * h20);
  const A02 = h10 * h21 - h11 * h20;
  const A10 = -(h01 * h22 - h02 * h21);
  const A11 = h00 * h22 - h02 * h20;
  const A12 = -(h00 * h21 - h01 * h20);
  const A20 = h01 * h12 - h02 * h11;
  const A21 = -(h00 * h12 - h02 * h10);
  const A22 = h00 * h11 - h01 * h10;

  const det = h00 * A00 + h01 * A01 + h02 * A02;
  if (Math.abs(det) < 1e-12) {
    return { h00: 1, h01: 0, h02: 0, h10: 0, h11: 1, h12: 0, h20: 0, h21: 0 };
  }
  const inv = 1 / det;
  const i22 = A22 * inv;
  return {
    h00: A00 * inv / i22, h01: A10 * inv / i22, h02: A20 * inv / i22,
    h10: A01 * inv / i22, h11: A11 * inv / i22, h12: A21 * inv / i22,
    h20: A02 * inv / i22, h21: A12 * inv / i22,
  };
}

/** Apply H to a point (x, y) in homogeneous coordinates (normalised). */
function applyH(H: HomographyMatrix, x: number, y: number): [number, number] {
  const { h00, h01, h02, h10, h11, h12, h20, h21 } = H;
  const w = h20 * x + h21 * y + 1;
  if (Math.abs(w) < 1e-12) return [0, 0];
  const ox = (h00 * x + h01 * y + h02) / w;
  const oy = (h10 * x + h11 * y + h12) / w;
  return [ox, oy];
}

function warpLayerTree(
  layers: readonly Layer[],
  Hinv: HomographyMatrix,
  outW: number,
  outH: number,
): Layer[] {
  return layers.map((layer) => {
    if (layer.kind === "raster") {
      return warpRasterLayer(layer, Hinv, outW, outH);
    }
    if (layer.kind === "group") {
      const gl = layer as GroupLayer;
      return {
        ...gl,
        children: warpLayerTree(gl.children, Hinv, outW, outH),
      };
    }
    return layer;
  });
}

function warpRasterLayer(
  layer: RasterLayer,
  Hinv: HomographyMatrix,
  outW: number,
  outH: number,
): RasterLayer {
  const src = layer.pixels;
  const srcW = src.width;
  const srcH = src.height;
  const lx = layer.x;
  const ly = layer.y;

  const dst = new PixelBuffer(outW, outH, src.mode ?? "rgba8");
  const srcData = src.data;
  const dstData = dst.data;

  for (let oy = 0; oy < outH; oy++) {
    for (let ox = 0; ox < outW; ox++) {
      // Map output pixel through inverse homography to get source pixel.
      const [sx, sy] = applyH(Hinv, ox, oy);

      // Convert to layer-local coordinates.
      const lsx = sx - lx;
      const lsy = sy - ly;

      const di = (oy * outW + ox) * 4;
      sampleBilinear(srcData, srcW, srcH, lsx, lsy, dstData, di);
    }
  }

  dst.markDirty();

  return {
    ...layer,
    x: 0,
    y: 0,
    pixels: dst,
  };
}

function sampleBilinear(
  src: Uint8ClampedArray,
  sw: number,
  sh: number,
  sx: number,
  sy: number,
  dst: Uint8ClampedArray,
  di: number,
): void {
  if (sx <= -1 || sy <= -1 || sx >= sw || sy >= sh) {
    dst[di] = dst[di + 1] = dst[di + 2] = dst[di + 3] = 0;
    return;
  }
  const x0 = Math.floor(sx);
  const y0 = Math.floor(sy);
  const x1 = x0 + 1;
  const y1 = y0 + 1;
  const fx = sx - x0;
  const fy = sy - y0;
  const w00 = (1 - fx) * (1 - fy);
  const w10 = fx * (1 - fy);
  const w01 = (1 - fx) * fy;
  const w11 = fx * fy;

  let r = 0, g = 0, b = 0, a = 0;
  const px = (x: number, y: number, ch: number) =>
    (x >= 0 && y >= 0 && x < sw && y < sh) ? (src[(y * sw + x) * 4 + ch] ?? 0) : 0;
  const pa = (x: number, y: number) =>
    (x >= 0 && y >= 0 && x < sw && y < sh) ? (src[(y * sw + x) * 4 + 3] ?? 0) : 0;

  const a00 = pa(x0, y0), a10 = pa(x1, y0), a01 = pa(x0, y1), a11 = pa(x1, y1);
  a = a00 * w00 + a10 * w10 + a01 * w01 + a11 * w11;
  if (a <= 0) {
    dst[di] = dst[di + 1] = dst[di + 2] = dst[di + 3] = 0;
    return;
  }
  for (let c = 0; c < 3; c++) {
    const v00 = px(x0, y0, c) * a00;
    const v10 = px(x1, y0, c) * a10;
    const v01 = px(x0, y1, c) * a01;
    const v11 = px(x1, y1, c) * a11;
    const sum = v00 * w00 + v10 * w10 + v01 * w01 + v11 * w11;
    dst[di + c] = Math.round(sum / a);
  }
  dst[di + 3] = Math.round(a);
}

// ---------------------------------------------------------------------------
// Gaussian elimination for 8×8 system
// ---------------------------------------------------------------------------

function gaussSolve(A: number[][], b: number[]): number[] | null {
  const n = 8;
  // Augmented matrix [A|b]
  const M: number[][] = A.map((row, i) => [...row, b[i]!]);

  for (let col = 0; col < n; col++) {
    // Partial pivot.
    let maxRow = col;
    let maxVal = Math.abs(M[col]![col]!);
    for (let row = col + 1; row < n; row++) {
      const v = Math.abs(M[row]![col]!);
      if (v > maxVal) { maxVal = v; maxRow = row; }
    }
    if (maxVal < 1e-12) return null;
    [M[col], M[maxRow]] = [M[maxRow]!, M[col]!];

    const pivot = M[col]![col]!;
    for (let row = col + 1; row < n; row++) {
      const factor = M[row]![col]! / pivot;
      for (let k = col; k <= n; k++) {
        M[row]![k]! -= factor * M[col]![k]!;
      }
    }
  }

  // Back substitution.
  const x = new Array<number>(n).fill(0);
  for (let i = n - 1; i >= 0; i--) {
    let sum = M[i]![n]!;
    for (let j = i + 1; j < n; j++) {
      sum -= M[i]![j]! * x[j]!;
    }
    x[i] = sum / M[i]![i]!;
  }
  return x;
}
